import { CreateEmployeePayload, UpdateEmployeePayload, useGetDataForCreateUserQuery } from 'src/api/endpoints/users'
import { Formik, useFormik } from 'formik'
import { requireField } from 'src/constants/constants'
import { generatePass } from 'src/utils/password/password'
import { FormItem } from 'src/components/Form'
import { InputText } from 'primereact/inputtext'
import { Dropdown } from 'src/components/Dropdown'
import { map } from 'lodash'
import { Button } from 'primereact/button'
import { ReactElement } from 'react'
import { EmployeeView } from 'src/api/endpoints/employees/api'
import classNames from 'classnames'
import { LOGIN_REGEX } from 'src/pages/edu/Students';

interface ModalContentProps {
    employee: EmployeeView | null;
    onDeactivate: (employee: EmployeeView) => void;
    onActivate: (employee: EmployeeView) => void;
    initialState:
        | (Partial<UpdateEmployeePayload> & Partial<CreateEmployeePayload>)
        | null;
    onSubmit: (form: UpdateEmployeePayload & CreateEmployeePayload) => Promise<void>;
    loading: boolean;
    directorCompany: boolean;
    readOnly: boolean;
}

export const ModalContent = (props: ModalContentProps): ReactElement => {
    const {
        loading,
        onSubmit,
        initialState,
        directorCompany,
        employee,
        onActivate,
        onDeactivate,
        readOnly,
    } = props
    
    const formik: Formik = useFormik<
        Partial<UpdateEmployeePayload> & Partial<CreateEmployeePayload>
    >({
        initialValues: {
            name: undefined,
            surname: undefined,
            login: '',
            password: undefined,
            role: undefined,
            filial: undefined,
            ...initialState,
        },
        validate: ({ login, password, role, filial, name, surname }) => {
            const errors: Partial<Record<keyof CreateEmployeePayload, any>> = {}

            if (!initialState) {
                if (!login) {
                    errors.login = requireField;
                } else if (login.trim().length < 6) {
                    errors.login = 'Логин должен быть не менее 6 символов';
                } else if (!login.trim().match(LOGIN_REGEX)) {
                    errors.login =
                        'Логин не может иметь кирриллические символы и пробелы';
                }
            }
            
            if (!initialState && !password) errors.password = requireField
            if (directorCompany && !filial) errors.filial = requireField
            if (!role) errors.role = requireField
            if (!name) errors.name = requireField
            if (!surname) errors.surname = requireField
            return errors
        },
        onSubmit: (values: Partial<UpdateEmployeePayload> & Partial<CreateEmployeePayload>): void => {
            onSubmit(values as UpdateEmployeePayload & CreateEmployeePayload).then(
                (): void => {
                    formik.resetForm()
                },
            )
        },
    })
    
    const generatePassword: VoidFunction = (): void => {
        formik.setFieldValue('password', generatePass())
    }
    
    const { login, password, filial, role, surname, name } = formik.values
    const { data, isLoading, isFetching } = useGetDataForCreateUserQuery()
    
    return (
        <form onSubmit={formik.handleSubmit} className="p-fluid">
            {initialState ? null : (
                <FormItem
                    value={login}
                    name={'login'}
                    label={'Логин'}
                    formik={formik}
                >
                    <InputText disabled={readOnly} />
                </FormItem>
            )}
            <FormItem value={name} name={'name'} label={'Имя'} formik={formik}>
                <InputText disabled={readOnly} />
            </FormItem>
            <FormItem
                value={surname}
                name={'surname'}
                label={'Фамилия'}
                formik={formik}
            >
                <InputText disabled={readOnly} />
            </FormItem>
            {directorCompany ? (
                <FormItem
                    value={filial}
                    name={'filial'}
                    label={'Филиал'}
                    formik={formik}
                >
                    <Dropdown
                        disabled={readOnly}
                        loading={isLoading || isFetching}
                        options={map(data?.filials, ({ id, name }) => ({
                            value: id,
                            label: name,
                        }))}
                    />
                </FormItem>
            ) : null}
            <FormItem value={role} name={'role'} label={'Роль'} formik={formik}>
                <Dropdown
                    disabled={readOnly}
                    loading={isLoading || isFetching}
                    options={map(data?.roles, ({ id, name }) => ({
                        value: id,
                        label: name,
                    }))}
                />
            </FormItem>
            {!readOnly && (<>
                <FormItem
                    value={password}
                    name={'password'}
                    label={'Пароль'}
                    formik={formik}
                >
                    <InputText />
                </FormItem>
                <Button
                    type={'button'}
                    className={'mb-24'}
                    style={{ width: 'auto', display: 'block' }}
                    onClick={generatePassword}
                >
                    Сгенерировать пароль
                </Button>
            </>)}
            <div className={classNames('modal-buttons', 'w-full')} style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '10px',
                justifyContent: 'end',
            }}>
                <Button
                    loading={loading}
                    disabled={readOnly}
                    type={'submit'}
                    label={initialState ? 'Обновить' : 'Сохранить'}
                    className={classNames('w-auto')}
                />
                {employee?.canBeActivated && (<Button
                    loading={loading}
                    onClick={(): void => onActivate(employee)}
                    type="button"
                    label={'Активировать'}
                    className={'w-auto'}
                />)}
                {employee?.canBeDeactivated && (<Button
                    loading={loading}
                    onClick={(): void => onDeactivate(employee)}
                    type="button"
                    label={'Деактивировать'}
                    className={'w-auto'}
                />)}
            </div>
        </form>
    )
}
